<script setup lang="ts">
import { computed, ref } from 'vue';
import { useElementSize } from '@vueuse/core';

import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import AppFoldableTransition from '../transitions/appFoldableTransition/AppFoldableTransition.vue';

const isExpanded = ref<boolean>(false);

const chevronIcon = computed<'chevronDown' | 'chevronUp'>(() =>
  isExpanded.value ? 'chevronUp' : 'chevronDown'
);
const contentRef = ref<HTMLDivElement | undefined>(undefined);
const { height } = useElementSize(contentRef);

function toggleExpanded(): void {
  isExpanded.value = !isExpanded.value;
}
</script>

<template>
  <div class="app-expandable-button">
    <button
      class="expandable-button"
      :aria-expanded="isExpanded"
      @click="toggleExpanded"
    >
      <div class="expandable-button-wrapper">
        <div class="content">
          <div class="summary-content">
            <div class="title">
              <slot name="title" />
            </div>
            <div class="subtitle">
              <slot name="subtitle" />
            </div>
          </div>
        </div>
        <AppSvgIcon :name="chevronIcon" size="16" color="color-soft-black" />
      </div>
    </button>

    <AppFoldableTransition :height="height">
      <div v-if="isExpanded">
        <div ref="contentRef">
          <slot name="content" />
        </div>
      </div>
    </AppFoldableTransition>
  </div>
</template>

<style scoped lang="scss">
@import '../../styles/_fonts.scss';

.app-expandable-button {
  .expandable-button {
    all: unset;
    width: 100%;
    cursor: pointer;

    .expandable-button-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--spacing-8);

      .content {
        flex: 1;
        min-width: 0;
        overflow: hidden;

        .summary-content {
          .title {
            @include font-label-m-bold;
            color: var(--color-soft-black);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .subtitle {
            @include font-label-s-light;
            color: var(--color-neutral-70);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }
  }
}
</style>
